import React, { useState } from 'react';
import {
  AgentVersionSummary,
  VersionChange,
  VersionChangeType,
  useCompareAgentVersionsQuery,
  useListAgentVersionsQuery,
  usePublishAgentVersionMutation,
  useRestoreAgentVersionMutation,
} from '@/redux/internal-services/agentsApi';
import { Modal, relTime, lineDiff, labelCls, inputCls } from './agentShared';
import { describeChange, sectionLabel, summarizeChanges } from './describeChange';
import { getErrorDetail } from '@/utils/apiError';
import { Alert } from '@/components/UI/Alert';
import { Badge, type BadgeProps } from '@/components/UI/Badge';
import { Button } from '@/components/UI/Button';
import { EmptyState } from '@/components/UI/EmptyState';
import { LoadingState } from '@/components/UI/LoadingState';
import { Select } from '@/components/UI/Select';
import { cn } from '@/utils/cn';

// ── helpers ────────────────────────────────────────────────────────────────────

const when = (iso?: string | null) => (iso ? new Date(iso).toLocaleString() : '');

const SOURCE_BADGE: Record<string, { label: string; variant: BadgeProps['variant']; title: string }> = {
  migrated: { label: 'Earlier history', variant: 'steel', title: 'Recorded by the old history, which kept only some fields.' },
  external: { label: 'Changed outside the editor', variant: 'warning', title: 'A seed, import or script changed the live agent directly.' },
  carried: { label: 'Draft carried over', variant: 'steel', title: 'An unpublished draft, rebuilt on top of a change made outside the editor.' },
  baseline: { label: 'History starts here', variant: 'steel', title: 'The configuration that was live when history tracking began.' },
};

const CHANGE_BADGE: Record<VersionChangeType, { label: string; variant: BadgeProps['variant'] }> = {
  added: { label: 'Added', variant: 'success' },
  removed: { label: 'Removed', variant: 'danger' },
  modified: { label: 'Modified', variant: 'warning' },
  reordered: { label: 'Reordered', variant: 'accent' },
};

const prettyKey = (s: string) => s.replace(/_/g, ' ');

const formatValue = (v: unknown): string => {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v === '' ? '(empty)' : v;
  return JSON.stringify(v, null, 2);
};

// ── comparison view ────────────────────────────────────────────────────────────

const ValueBox: React.FC<{ tone: 'add' | 'del'; label: string; value: unknown }> = ({ tone, label, value }) => (
  <div>
    <div className="eyebrow mb-0.5">{label}</div>
    <pre
      className={cn(
        'max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-control border px-2 py-1.5 font-mono text-xs leading-relaxed',
        tone === 'add' ? 'border-success/30 bg-success/10 text-success-text' : 'border-danger/30 bg-danger/10 text-danger-text',
      )}
    >
      {formatValue(value)}
    </pre>
  </div>
);

const TextDiff: React.FC<{ before: string; after: string }> = ({ before, after }) => {
  const lines = lineDiff(before, after);
  return (
    <div className="max-h-64 overflow-auto rounded-control border border-ui-border bg-ui-subtle p-2 font-mono text-xs leading-relaxed">
      {lines.map((l, i) => (
        <div
          key={i}
          className={cn(
            'whitespace-pre-wrap break-words',
            l.t === 'add' && 'bg-success/10 text-success-text',
            l.t === 'del' && 'bg-danger/10 text-danger-text',
            l.t === 'same' && 'text-ui-text2',
          )}
        >
          <span className="select-none pr-1 text-ui-text3">{l.t === 'add' ? '+' : l.t === 'del' ? '−' : ' '}</span>
          {l.v || ' '}
        </div>
      ))}
    </div>
  );
};

const OrderList: React.FC<{ title: string; items: unknown; moved: Set<string> }> = ({ title, items, moved }) => (
  <div className="min-w-0 flex-1">
    <div className="eyebrow mb-0.5">{title}</div>
    <ol className="space-y-0.5 rounded-control border border-ui-border bg-ui-subtle p-2 text-xs">
      {(Array.isArray(items) ? items : []).map((label, i) => (
        <li
          key={`${label}-${i}`}
          className={cn('flex gap-2 rounded-chip px-1', moved.has(String(label)) ? 'bg-accent-soft font-medium text-accent-text' : 'text-ui-text2')}
        >
          <span className="w-5 shrink-0 text-right font-mono text-ui-text3">{i + 1}</span>
          <span className="min-w-0 break-words">{String(label)}</span>
        </li>
      ))}
    </ol>
  </div>
);

const ChangeRow: React.FC<{ change: VersionChange }> = ({ change }) => {
  const badge = CHANGE_BADGE[change.type];
  // The section is the group heading, so the row only needs the rest of the path.
  const rest = change.path.slice(1);
  const bothText = typeof change.old === 'string' && typeof change.new === 'string';
  const longText = bothText && ((change.old as string).includes('\n') || (change.new as string).includes('\n') || (change.old as string).length > 80 || (change.new as string).length > 80);
  return (
    <div className="space-y-1.5 rounded-control border border-ui-border bg-ui-card p-3">
      <div className="flex flex-wrap items-start gap-2">
        <Badge variant={badge.variant}>{badge.label}</Badge>
        <div className="min-w-0 flex-1">
          <div className="break-words text-sm font-medium text-ui-text">{describeChange(change)}</div>
          <div className="break-words font-mono text-micro text-ui-text3" title="Where in the configuration">
            {[sectionLabel(change.section), ...rest.map(prettyKey)].join(' › ')}
          </div>
        </div>
      </div>
      {change.type === 'modified' && (longText ? (
        <TextDiff before={change.old as string} after={change.new as string} />
      ) : (
        <div className="grid gap-2 sm:grid-cols-2">
          <ValueBox tone="del" label="Previous value" value={change.old} />
          <ValueBox tone="add" label="New value" value={change.new} />
        </div>
      ))}
      {change.type === 'added' && <ValueBox tone="add" label="New value" value={change.new} />}
      {change.type === 'removed' && <ValueBox tone="del" label="Previous value" value={change.old} />}
      {change.type === 'reordered' && (
        <>
          <div className="flex flex-col gap-2 sm:flex-row">
            <OrderList title="Before" items={change.old} moved={new Set((change.moves ?? []).map((m) => m.label))} />
            <OrderList title="After" items={change.new} moved={new Set((change.moves ?? []).map((m) => m.label))} />
          </div>
          {(change.moves ?? []).length > 0 && (
            <p className="text-xs text-ui-text3">
              Moved: {(change.moves ?? []).map((m) => `${m.label} (${m.from + 1} → ${m.to + 1})`).join(', ')}
            </p>
          )}
        </>
      )}
    </div>
  );
};

interface ChangesModalProps {
  agentId: string;
  /** A version number, or 'previous' for the version saved just before `to`. */
  from: number | 'previous';
  to: number;
  onClose: () => void;
  footer?: React.ReactNode;
}

export const VersionChangesModal: React.FC<ChangesModalProps> = ({ agentId, from, to, onClose, footer }) => {
  const { data, isLoading, error } = useCompareAgentVersionsQuery({ id: agentId, from, to });
  const groups = (data?.changes ?? []).reduce<Record<string, VersionChange[]>>((acc, c) => {
    (acc[c.section || 'other'] ??= []).push(c);
    return acc;
  }, {});
  const counts = (data?.changes ?? []).reduce<Partial<Record<VersionChangeType, number>>>((acc, c) => {
    acc[c.type] = (acc[c.type] ?? 0) + 1;
    return acc;
  }, {});
  return (
    <Modal
      wide
      title={typeof from === 'number' ? `Changes: v${from} → v${to}` : `Changes in version ${to}`}
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
          {footer}
        </>
      }
    >
      {isLoading && <LoadingState label="Comparing versions…" />}
      {error != null && <Alert tone="danger">{getErrorDetail(error) ?? 'Could not load the changes.'}</Alert>}
      {data && (
        <div className="space-y-3">
          <div className="text-sm">
            <span className="font-medium text-ui-text">{data.to.comment}</span>
            <div className="text-xs text-ui-text3">
              v{data.to.version} · {data.to.created_by_name ?? 'Unknown'} · {when(data.to.created_at)}
              {data.from ? ` · compared with v${data.from.version}` : ' · first version: everything is new'}
            </div>
          </div>
          {data.note && <Alert tone="warning">{data.note}</Alert>}
          {data.changes.length === 0 ? (
            <EmptyState variant="inline" title="No differences between these versions." />
          ) : (
            <>
              <div className="flex flex-wrap gap-1.5">
                {(Object.keys(CHANGE_BADGE) as VersionChangeType[]).filter((t) => counts[t]).map((t) => (
                  <Badge key={t} variant={CHANGE_BADGE[t].variant}>{`${counts[t]} ${CHANGE_BADGE[t].label.toLowerCase()}`}</Badge>
                ))}
              </div>
              {Object.entries(groups).map(([section, changes]) => (
                <div key={section} className="space-y-2">
                  <div className="eyebrow">{sectionLabel(section)}</div>
                  {changes.map((c, i) => <ChangeRow key={`${c.path_text}-${c.type}-${i}`} change={c} />)}
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </Modal>
  );
};

// ── Save (comment) modal ───────────────────────────────────────────────────────

interface SaveVersionModalProps {
  nextVersion: number;
  saving: boolean;
  error?: string;
  /** Offered when saving an existing agent that is not already live at its latest version. */
  onClose: () => void;
  onSave: (comment: string, publish: boolean) => void;
}

export const SaveVersionModal: React.FC<SaveVersionModalProps> = ({ nextVersion, saving, error, onClose, onSave }) => {
  const [comment, setComment] = useState('');
  const [publish, setPublish] = useState(false);
  const submit = () => !saving && onSave(comment.trim(), publish);
  return (
    <Modal
      title={`Save as version ${nextVersion}`}
      onClose={saving ? () => undefined : onClose}
      footer={
        <>
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" size="sm" onClick={submit} disabled={saving}>
            {saving ? 'Saving…' : 'Save version'}
          </Button>
        </>
      }
    >
      <div className="mb-4">
        <label className={labelCls} htmlFor="save-version-comment">
          What did you change? <span className="font-normal text-ui-text3">— kept with this version in History</span>
        </label>
        <input
          id="save-version-comment"
          autoFocus
          className={inputCls}
          value={comment}
          maxLength={300}
          onChange={(e) => setComment(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            }
          }}
          placeholder="e.g. Tightened the refund rules, added the lookup tool"
        />
        <p className="mt-1 text-xs text-ui-text3">Leave it empty and the version is named after what changed.</p>
      </div>
      <label className="flex cursor-pointer items-start gap-2.5 rounded-control border border-ui-border px-3 py-2.5 text-sm">
        <input type="checkbox" checked={publish} onChange={(e) => setPublish(e.target.checked)} className="mt-0.5 h-4 w-4 accent-accent" />
        <span>
          Also make it live
          <span className="block text-xs text-ui-text3">Off by default: saving only records the version. Publish it later from History.</span>
        </span>
      </label>
      {error && <Alert tone="danger" className="mt-3">{error}</Alert>}
    </Modal>
  );
};

// ── per-row summary in plain words ─────────────────────────────────────────────

/** "What changed" for one version row: the changes against the previous version, one plain sentence each. */
const ChangeSummary: React.FC<{ agentId: string; version: AgentVersionSummary; startOpen: boolean; onSeeAll: () => void }> = ({
  agentId, version, startOpen, onSeeAll,
}) => {
  const [open, setOpen] = useState(startOpen);
  const starting = ['created', 'baseline'].includes(version.source);
  const { data, isLoading, error } = useCompareAgentVersionsQuery(
    { id: agentId, from: 'previous', to: version.version },
    { skip: !open || starting || version.source === 'migrated' },
  );
  if (starting) return <p className="mt-1.5 text-xs text-ui-text3">Starting point of this history: the configuration as it was.</p>;
  if (version.source === 'migrated') {
    return <p className="mt-1.5 text-xs text-ui-text3">Recorded by the old history, which kept only some fields. Use Changes to compare.</p>;
  }
  const { lines, more } = summarizeChanges(data?.changes ?? []);
  return (
    <div className="mt-1.5">
      <button
        type="button"
        className="text-xs font-medium text-accent-text hover:underline focus-ring rounded-chip"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {open ? '▾ What changed' : '▸ What changed'}
      </button>
      {open && (
        <div className="mt-1">
          {isLoading && <span className="text-xs text-ui-text3">Loading…</span>}
          {error != null && <span className="text-xs text-danger-text">Could not load the changes.</span>}
          {data && lines.length === 0 && <span className="text-xs text-ui-text3">No differences from the previous version.</span>}
          {lines.length > 0 && (
            <ul className="list-disc space-y-0.5 pl-5 text-xs text-ui-text2">
              {lines.map((l, i) => <li key={`${i}-${l}`} className="break-words">{l}</li>)}
              {more > 0 && (
                <li className="list-none -ml-5 pl-5">
                  <button type="button" className="font-medium text-accent-text hover:underline focus-ring rounded-chip" onClick={onSeeAll}>
                    {`…and ${more} more: see every change`}
                  </button>
                </li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

// ── History tab ────────────────────────────────────────────────────────────────

interface VersionsTabProps {
  agentId: string;
  headVersion?: number;
  /** The form holds edits that are not saved yet; creating a new version from an old one reloads the form from the server and would drop them. */
  hasUnsavedEdits: boolean;
}

type Pending =
  | null
  | { kind: 'changes'; from: number | 'previous'; to: number }
  | { kind: 'restore'; version: AgentVersionSummary }
  | { kind: 'publish'; version: AgentVersionSummary };

export const VersionsTab: React.FC<VersionsTabProps> = ({ agentId, headVersion, hasUnsavedEdits }) => {
  const [limit, setLimit] = useState(50);
  const { data, isLoading, error } = useListAgentVersionsQuery({ id: agentId, limit });
  const [publishVersion, { isLoading: publishing }] = usePublishAgentVersionMutation();
  const [restoreVersion, { isLoading: restoring }] = useRestoreAgentVersionMutation();
  const [pending, setPending] = useState<Pending>(null);
  const [restoreComment, setRestoreComment] = useState('');
  const [actionError, setActionError] = useState('');
  const [notice, setNotice] = useState('');
  const [cmpFrom, setCmpFrom] = useState('');
  const [cmpTo, setCmpTo] = useState('');

  const items = data?.items ?? [];
  const published = data?.published_version ?? null;
  const head = data?.head_version ?? headVersion;

  const open = (p: Pending) => {
    setActionError('');
    setPending(p);
    if (p?.kind === 'restore') setRestoreComment(`New version from v${p.version.version}`);
  };

  const doRestore = async (v: AgentVersionSummary) => {
    setActionError('');
    try {
      const res = await restoreVersion({ id: agentId, version: v.version, comment: restoreComment.trim() || undefined, base_version: head }).unwrap();
      setPending(null);
      setNotice(
        res.version_created === false
          ? `The latest version already matches v${v.version}, so nothing was created.`
          : `Created v${res.head_version} from v${v.version}. Every earlier version is unchanged. Publish v${res.head_version} to make it live.`,
      );
    } catch (err) {
      setActionError(getErrorDetail(err) ?? 'Could not create the new version.');
    }
  };

  const doPublish = async (v: AgentVersionSummary) => {
    setActionError('');
    try {
      await publishVersion({ id: agentId, version: v.version }).unwrap();
      setPending(null);
      setNotice(`v${v.version} is now live.`);
    } catch (err) {
      setActionError(getErrorDetail(err) ?? 'Publish failed.');
    }
  };

  const compare = () => {
    const a = Number(cmpFrom);
    const b = Number(cmpTo);
    if (!a || !b || a === b) return;
    open({ kind: 'changes', from: Math.min(a, b), to: Math.max(a, b) });
  };

  const rowActions = (v: AgentVersionSummary): React.ReactNode => (
    <>
      {!v.is_published && !v.partial && (
        <Button type="button" size="sm" onClick={() => open({ kind: 'publish', version: v })}>
          Publish
        </Button>
      )}
      {!v.is_head && (
        <Button type="button" variant="outline" size="sm" onClick={() => open({ kind: 'restore', version: v })}>
          New version from this
        </Button>
      )}
    </>
  );

  return (
    <div>
      <p className="mb-3 text-sm text-ui-text2">
        Every save creates a version, including ones never published. <strong>Publish</strong> chooses which version the live agent uses.
        <strong> New version from this</strong> copies an older version into a new one; nothing is ever deleted or rewritten.
      </p>

      <div
        className={cn(
          'mb-4 flex flex-wrap items-center gap-3 rounded-card border p-4',
          hasUnsavedEdits || (head != null && published !== head) ? 'border-warning/40 bg-warning/10' : 'border-ui-border bg-ui-card',
        )}
      >
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-ui-text">
            {hasUnsavedEdits ? 'You have unsaved edits' : head != null && published !== head ? `v${head} is saved but not live` : 'Everything saved is live'}
          </div>
          <div className="text-xs text-ui-text2">
            {published != null ? `Live: v${published}` : 'Nothing published yet'}
            {head != null ? ` · Latest saved: v${head}` : ''}
            {hasUnsavedEdits ? ' · Use Save Changes to record them as a new version.' : ''}
          </div>
        </div>
      </div>

      {notice && (
        <Alert tone="success" className="mb-3">
          {notice}
        </Alert>
      )}

      {items.length > 1 && (
        <div className="mb-4 flex flex-wrap items-end gap-2">
          <div>
            <label className={labelCls} htmlFor="cmp-from">Compare</label>
            <Select id="cmp-from" value={cmpFrom} onChange={(e) => setCmpFrom(e.target.value)} wrapperClassName="w-28">
              <option value="">version…</option>
              {items.map((v) => <option key={v.version} value={v.version}>{`v${v.version}`}</option>)}
            </Select>
          </div>
          <div>
            <label className={labelCls} htmlFor="cmp-to">with</label>
            <Select id="cmp-to" value={cmpTo} onChange={(e) => setCmpTo(e.target.value)} wrapperClassName="w-28">
              <option value="">version…</option>
              {items.map((v) => <option key={v.version} value={v.version}>{`v${v.version}`}</option>)}
            </Select>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={compare} disabled={!cmpFrom || !cmpTo || cmpFrom === cmpTo}>
            Show differences
          </Button>
        </div>
      )}

      {isLoading && <LoadingState label="Loading history…" />}
      {error != null && <Alert tone="danger">{getErrorDetail(error) ?? 'Could not load the history.'}</Alert>}
      {!isLoading && !error && items.length === 0 && <EmptyState variant="inline" title="No versions yet." />}

      <ul className="space-y-2">
        {items.map((v) => {
          const src = SOURCE_BADGE[v.source];
          return (
            <li key={v.version} className="card-surface px-4 py-3" data-testid={`version-row-${v.version}`}>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                <div className="flex min-w-0 flex-1 items-start gap-3">
                <span className="rounded-chip bg-ui-subtle px-2 py-0.5 font-mono text-xs font-medium text-ui-text">v{v.version}</span>
                <div className="min-w-0 flex-1">
                  <div className="break-words text-sm font-medium text-ui-text">{v.comment}</div>
                  <div className="text-xs text-ui-text3">
                    {v.created_by_name ?? 'Unknown'} · {when(v.created_at)} <span className="text-ui-text3">({relTime(v.created_at)})</span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    {v.is_published && <Badge variant="success">● Live</Badge>}
                    {v.is_head && !v.is_published && <Badge variant="accent">Latest, not live</Badge>}
                    {v.restored_from != null && <Badge variant="neutral">{`Made from v${v.restored_from}`}</Badge>}
                    {src && <Badge variant={src.variant} title={src.title}>{src.label}</Badge>}
                  </div>
                  <ChangeSummary
                    agentId={agentId}
                    version={v}
                    startOpen={items.indexOf(v) < 5}
                    onSeeAll={() => open({ kind: 'changes', from: 'previous', to: v.version })}
                  />
                </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                  <Button type="button" variant="outline" size="sm" onClick={() => open({ kind: 'changes', from: 'previous', to: v.version })}>
                    Changes
                  </Button>
                  {rowActions(v)}
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {data && data.total > items.length && (
        <div className="mt-3 text-center">
          <Button type="button" variant="outline" size="sm" onClick={() => setLimit((l) => l + 50)}>
            {`Show older versions (${data.total - items.length} more)`}
          </Button>
        </div>
      )}

      {pending?.kind === 'changes' && (
        <VersionChangesModal
          agentId={agentId}
          from={pending.from}
          to={pending.to}
          onClose={() => setPending(null)}
          footer={(() => {
            const target = items.find((v) => v.version === pending.to);
            return target ? rowActions(target) : null;
          })()}
        />
      )}

      {pending?.kind === 'restore' && (
        <Modal
          title={`New version from v${pending.version.version}`}
          onClose={() => !restoring && setPending(null)}
          footer={
            <>
              <Button type="button" variant="outline" size="sm" onClick={() => setPending(null)} disabled={restoring}>
                Cancel
              </Button>
              <Button type="button" size="sm" onClick={() => doRestore(pending.version)} disabled={restoring}>
                {restoring ? 'Creating…' : 'Create new version'}
              </Button>
            </>
          }
        >
          <p className="mb-3 text-sm text-ui-text2">
            This creates <strong>a new version</strong> with the same settings as v{pending.version.version}. Every existing version, including
            the current one, stays exactly as it is, and the live agent does not change until you publish the new one.
          </p>
          <label className={labelCls} htmlFor="restore-comment">Comment</label>
          <input
            id="restore-comment"
            autoFocus
            className={inputCls}
            value={restoreComment}
            maxLength={300}
            onChange={(e) => setRestoreComment(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (!restoring) void doRestore(pending.version);
              }
            }}
          />
          {pending.version.partial && (
            <Alert tone="warning" className="mt-3">
              This version came from the old history, which kept only some fields. Only those are put back; every other field stays as it is now.
            </Alert>
          )}
          {hasUnsavedEdits && (
            <Alert tone="warning" className="mt-3">
              You have unsaved edits in the editor. Creating the new version reloads the editor from the saved versions, so those edits will be lost.
            </Alert>
          )}
          {actionError && <Alert tone="danger" className="mt-3">{actionError}</Alert>}
        </Modal>
      )}

      {pending?.kind === 'publish' && (
        <Modal
          title={`Publish version ${pending.version.version}`}
          onClose={() => !publishing && setPending(null)}
          footer={
            <>
              <Button type="button" variant="outline" size="sm" onClick={() => setPending(null)} disabled={publishing}>
                Cancel
              </Button>
              <Button type="button" size="sm" onClick={() => doPublish(pending.version)} disabled={publishing}>
                {publishing ? 'Publishing…' : 'Make it live'}
              </Button>
            </>
          }
        >
          <p className="text-sm text-ui-text2">
            The live agent will start using the configuration of <strong>v{pending.version.version}</strong> right away
            {published != null ? <> instead of v{published}</> : null}. No version is created or removed.
          </p>
          <p className="mt-2 text-xs text-ui-text3">“{pending.version.comment}”</p>
          {actionError && <Alert tone="danger" className="mt-3">{actionError}</Alert>}
        </Modal>
      )}
    </div>
  );
};
